import { FunctionComponent, ReactNode } from "react";

import { faCircleInfo } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components/Icon/Icon";
import { Tooltip } from "@/components/Tooltip/Tooltip";

interface ChatFieldRowProps {
	label: string;
	/** The `?` hover body. Omit for a field the mockup gives no tooltip (e.g. Allowed senders). */
	tooltip?: ReactNode;
	/** One line explaining what the setting does in practice. */
	description: string;
	/**
	 * Put the description directly under the label instead of under the control. The mockup does this
	 * for a field whose control is a tall block rather than a single pill (Allowed senders, whose
	 * body is the sender pool *and* the routing disclosure): left below the control, the sentence ends
	 * up hundreds of pixels from the label it belongs to and reads as describing whatever sits above
	 * it. Single-control rows keep the description underneath, as the mockup also shows.
	 */
	descriptionAboveControl?: boolean;
	children: ReactNode;
}

/**
 * One labelled setting in the Chat section: title (+ optional `?` tooltip), the control, and a
 * one-line description. Its own component so `ChatSection` stays composition and the three fields
 * cannot drift apart visually.
 */
export const ChatFieldRow: FunctionComponent<ChatFieldRowProps> = ({
	label,
	tooltip,
	description,
	descriptionAboveControl = false,
	children,
}) => {
	const descriptionLine = (
		<p
			className={
				descriptionAboveControl
					? "mb-3 text-xs leading-5 text-text-meta"
					: "mt-2 text-xs leading-5 text-text-meta"
			}
		>
			{description}
		</p>
	);

	return (
		<div className="border-t border-border-light py-4">
			<div
				className={
					descriptionAboveControl ? "flex items-center gap-2" : "mb-2 flex items-center gap-2"
				}
			>
				<span className="text-sm font-semibold text-text">{label}</span>
				{tooltip ? (
					<Tooltip content={tooltip}>
						<Icon className="text-gray-400" icon={faCircleInfo} />
					</Tooltip>
				) : null}
			</div>
			{descriptionAboveControl && descriptionLine}
			{children}
			{!descriptionAboveControl && descriptionLine}
		</div>
	);
};
